import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Sphere } from 'types/global';
import { PermissionList } from 'types/permissions';
import UrgencyPillMolecule from '../../UrgencyPill/UrgencyPill';

interface AtlasUrgencyPillProps {
  isOpen: boolean;
  onClose: () => void;
  onUrgencyChange: (urgency: string, edgeId: string) => void;
  urgencyValue: number;
  parentNode?: Sphere;
  edgeId?: string;
  fetchPermissionList: (
    sphereId: string,
    nodeLabel: string,
    permissions?: string[]
  ) => Promise<PermissionList | null>;
}

const AtlasUrgencyPill: React.FC<AtlasUrgencyPillProps> = ({
  isOpen,
  onClose,
  onUrgencyChange,
  urgencyValue,
  parentNode,
  edgeId,
  fetchPermissionList,
}) => {
  const pillRef = useRef<HTMLDivElement>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  // Check permissions before rendering the UrgencyPill
  const checkPermission = useCallback(async () => {
    if (!parentNode?.id) {
      setHasPermission(true);
      return;
    }
    try {
      setHasPermission(null); // Indeterminate state
      const permissionList = await fetchPermissionList(
        parentNode.id,
        parentNode.label ?? '',
        ['edit']
      );
      const canEdit = permissionList?.permissions?.edit ?? false;
      if (canEdit) {
        setHasPermission(true);
      } else {
        onClose(); // Close if no permissions
      }
    } catch (error) {
      console.error('AtlasUrgencyPill: Permission check failed:', error);
      onClose();
    }
  }, [parentNode, fetchPermissionList, onClose]);

  useEffect(() => {
    if (isOpen) {
      checkPermission();
    }
  }, [isOpen, checkPermission]);

  useEffect(() => {
    const handleDocClick = (ev: MouseEvent) => {
      if (
        isOpen &&
        pillRef.current &&
        !pillRef.current.contains(ev.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
    };
  }, [isOpen, onClose]);

  if (hasPermission !== true || !isOpen) {
    return null; // Render nothing if permissions are not granted or not open
  }

  return (
    <div className="atlas-urgency-pill" ref={pillRef}>
      <UrgencyPillMolecule
        placement="popover"
        urgencyValue={urgencyValue}
        onUrgencyChange={(newUrgency) =>
          onUrgencyChange(newUrgency, edgeId ?? '')
        }
      />
    </div>
  );
};

export default AtlasUrgencyPill;
